import {gsap} from "gsap";
import {AnimUtils} from "../utils/AnimUtils";
import {ISymbolPosition} from "../../core/reel/IReelData";
import {SymbolMultiplier} from "../components/SymbolMultiplier";
import {HOTBData} from "../server/HOTBData";
import {HOTB} from "../HOTB";
import {GameSounds} from "../sound/GameSounds";
import {FeatureList, GameConfig, TopReelFeaturesList} from "../GameConfig";
import {GameUtils} from "../utils/GameUtils";
import {ExtendedGameMode} from "../server/ExtendedGameMode";

export enum SymbolStates {
    IDLE = "idle",
    SPIN = "spin",
    LANDING = "landing",
    NORMAL = "normal",
    WIN = "win",
    NO_WIN = "noWin",
    PRE_WIN = "preWin",
    TRIGGER = "trigger",
    BLAST = "blast",
    SHOW_LAUNCH_MULTI = "multiplierImpact"
}

export enum MidSymbolsList {
    M1 = "M1",
    M2 = "M2",
    M3 = "M3",
    M4 = "M4",
    M5 = "M5"
}

export enum LowSymbolsList {
    L1 = "L1",
    L2 = "L2",
    L3 = "L3",
    L4 = "L4",
    L5 = "L5"
}

export enum SpecialSymbolsList {
    WILD = "W",
    S1 = "S1",
    S2 = "S2",
    BLANK = "BLANK",
    LAUNCH = "LAUNCH",
    LAUNCH_WILD = "LAUNCH_W",
    XBOMB = "XBOMB",
    XNUDGE = "XNUDGE",
    MAGA_CAP = "MAGA_CAP"
}

export enum SymbolsList {
    M1 = MidSymbolsList.M1,
    M2 = MidSymbolsList.M2,
    M3 = MidSymbolsList.M3,
    M4 = MidSymbolsList.M4,
    M5 = MidSymbolsList.M5,
    L1 = LowSymbolsList.L1,
    L2 = LowSymbolsList.L2,
    L3 = LowSymbolsList.L3,
    L4 = LowSymbolsList.L4,
    L5 = LowSymbolsList.L5,
    WILD = SpecialSymbolsList.WILD,
    S1 = SpecialSymbolsList.S1,
    S2 = SpecialSymbolsList.S2,
    BLANK = SpecialSymbolsList.BLANK,
    LAUNCH = SpecialSymbolsList.LAUNCH,
    LAUNCH_WILD = SpecialSymbolsList.LAUNCH_WILD,
    XBOMB = SpecialSymbolsList.XBOMB,
    XNUDGE = SpecialSymbolsList.XNUDGE,
    MAGA_CAP = SpecialSymbolsList.MAGA_CAP
}

export class GameSymbol extends PIXI.Container {
    private _spine: PIXI.spine.Spine;
    private _nudgeMultiplier: SymbolMultiplier;
    private _symMultiplier: SymbolMultiplier;
    private _bobbleTL: gsap.core.Timeline | undefined;
    private _multiHitSoundPlayed: boolean = false;

    constructor(symbolName: string, initState: SymbolStates, symbolPositionData: ISymbolPosition, isNudgeSym?: boolean) {
        super();

        if (symbolName === undefined) {
            debugger;
            throw new Error("symbolName should not be undefined !!!");
        }

        this.symbolName = symbolName;
        this.reelId = symbolPositionData.reelId;
        this.isRemoved = false;
        this.isNudgeWild = isNudgeSym ? isNudgeSym : false;
        this.splitCount = 0;
        this.init();
        this.getStateAnimation(initState);
    }

    private _isBobbling: boolean = false;

    get isBobbling(): boolean {
        return this._isBobbling;
    }

    set isBobbling(value: boolean) {
        this._isBobbling = value;
    }

    private _splitCount: number = 0;

    get splitCount(): number {
        return this._splitCount;
    }

    set splitCount(value: number) {
        // restriction - split size can not grow beyond 4
        if (value > 4 || value === 1) {
            return;
        }
        if (!(this.symbolName === SymbolsList.S1 || this.symbolName === SymbolsList.S2)) {
            this._splitCount = value;
        }
    }

    private _currState: SymbolStates;

    get currState(): SymbolStates {
        return this._currState;
    }

    set currState(value: SymbolStates) {
        this._currState = value;
    }

    private _isNudgeWild: boolean;

    get isNudgeWild(): boolean {
        return this._isNudgeWild;
    }

    set isNudgeWild(value: boolean) {
        this._isNudgeWild = value;
    }

    private _isRemoved: boolean;

    public get isRemoved(): boolean {
        return this._isRemoved;
    }

    public set isRemoved(value: boolean) {
        this._isRemoved = value;
    }

    private _reelId: number;

    public get reelId(): number {
        return this._reelId;
    }

    public set reelId(value: number) {
        this._reelId = value;
    }

    private _reelPosition: number;

    public get reelPosition(): number {
        return this._reelPosition;
    }

    public set reelPosition(value: number) {
        this._reelPosition = value;
    }

    private _reelTargetPosition: number;

    public get reelTargetPosition(): number {
        return this._reelTargetPosition;
    }

    public set reelTargetPosition(value: number) {
        this._reelTargetPosition = value;
    }

    private _symbolName: string;

    get symbolName(): string {
        return this._symbolName;
    }

    set symbolName(symbolName: string) {
        this._symbolName = symbolName;
    }

    public startBobbleShake(): gsap.core.Timeline | undefined {
        if (this.name === TopReelFeaturesList.XNUDGE && this.reelTargetPosition !== 3) {
            return gsap.timeline();
        }

        if (this.isBobbling) {
            return this._bobbleTL;
        }

        this.isBobbling = true;

        this._bobbleTL = gsap.timeline({
            onStart: (): void => {
                this.isBobbling = true;
            }
        });

        if (this.symbolName !== SymbolsList.LAUNCH) {
            this._bobbleTL.add(this.getStateAnimation(SymbolStates.PRE_WIN));
        }

        this._bobbleTL.to(this, {
            rotation: (this.symbolName === SymbolsList.XNUDGE) ? GameConfig.nudgeBobbleRotation : GameConfig.normalBobbleRotation,
            duration: GameConfig.bobbleDuration,
            yoyo: true,
            repeat: -1,
            ease: "sine.inOut"
        }).to(this, {
            rotation: (this.symbolName === SymbolsList.XNUDGE) ? -(GameConfig.nudgeBobbleRotation) : -(GameConfig.normalBobbleRotation),
            duration: GameConfig.bobbleDuration,
            yoyo: true,
            repeat: -1,
            ease: "sine.inOut"
        });

        return this._bobbleTL;
    }

    public resetBobble(): void {
        if (this._bobbleTL && this._bobbleTL.isActive()) {
            this._bobbleTL.progress(1);
            this._bobbleTL.pause();
            this._bobbleTL.kill();
            this._bobbleTL = undefined;
            this.isBobbling = false;
            this.rotation = 0;
        }
    }

    public getStateAnimation(state: SymbolStates, isLastSymbol?: boolean): gsap.core.Timeline {
        const tl: gsap.core.Timeline = gsap.timeline(),
            skinsArr: string[] = [],
            animName: string = state;
        if (this.symbolName === SymbolsList.S1 || this.symbolName === SymbolsList.S2 || this.symbolName === SymbolsList.MAGA_CAP) {
            skinsArr.push("default");
        } else {
            skinsArr.push("xSplit_" + this.splitCount);
        }

        if (state === SymbolStates.LANDING) {
            if (this.symbolName === SymbolsList.S1) {
                GameConfig.TOTAL_SCATTER_COUNT++;
                GameConfig.scatterLandedInReels[this.reelId] = true;

                tl.add((): void => {
                    // do not play scatter land sound on reel 1 if boosted bet
                    // #418
                    const scatterSoundsCount: number = GameConfig.scatterSoundsPlayed.length;
                    if (!(HOTB.model.data.featureBuyName.includes("BOOSTED_BET") && this.reelId === 1)) {
                        HOTB.soundPlayer.playSound(GameSounds.SCATTER_LAND[scatterSoundsCount]);
                    }
                    GameConfig.scatterSoundsPlayed.push(true);

                    if (GameConfig.TOTAL_SCATTER_COUNT > 1) {
                        AnimUtils.playScreenShake(GameConfig.TOTAL_SCATTER_COUNT - 1);
                    }
                });
            } else if (this.symbolName === SymbolsList.S2) {
                GameConfig.TOTAL_SCATTER_COUNT++;
                tl.add((): void => {
                    HOTB.soundPlayer.playSound(GameSounds.SCATTER_UPGRADE);
                });
            }

            if (GameConfig.TOTAL_SCATTER_COUNT < 4 && GameConfig.TOTAL_SCATTER_COUNT >= 2 && !GameConfig.scatterLandedInReels[this.reelId]) {
                GameConfig.NEAR_SCATTER_WIN_DELAY += GameUtils.isFastSpin() ? GameConfig.scatterAnticipationDelayFastSpin : GameConfig.scatterAnticipationDelay;
            }
        }

        if (HOTB.model.data.featureBuyName.includes(ExtendedGameMode.FREESPIN)) {
            GameConfig.NEAR_SCATTER_WIN_DELAY = 0;
        }

        tl.add(AnimUtils.playSpineAnimation({
            spineObj: this._spine,
            animation: animName,
            skins: skinsArr,
            playLoop: state === SymbolStates.IDLE,
        })).eventCallback("onComplete", (): void => {
            this.currState = state;
        });

        if (state === SymbolStates.LANDING && this.symbolName === SymbolsList.S2) {
            tl.add([
                this.getStateAnimation(SymbolStates.WIN),
                (): void => {
                    HOTB.keypadPlugin.setZeroBetSpinCounter(HOTB.model.gameData.freespinsLeft);
                }
            ]);
        }
        return tl;
    }

    public showNudgeMulti(): void {
        if (this.isNudgeWild) {
            this._nudgeMultiplier.alpha = 1;
            this._nudgeMultiplier.position.set(0, -180);
            this._nudgeMultiplier.skew.set(0, -0.05);
        }
    }

    public updateNudgeMultiplier(value: number): void {
        if (this.isNudgeWild) {
            this._nudgeMultiplier.updateValue(value);
            if (this.splitCount === 2) {
                this._nudgeMultiplier.position.x = 7;
                this._nudgeMultiplier.skew.set(0, 0.05);
            } else {
                this._nudgeMultiplier.position.x = 5;
                this._nudgeMultiplier.skew.set(0, 0.2);
                this._nudgeMultiplier.rotation = 0.1;
            }
        }
    }

    public updateSymbolMultiplier(multValue: number): void {
        if (this.symbolName === SymbolsList.LAUNCH || this.symbolName === SymbolsList.WILD) {
            this._symMultiplier.updateValue(multValue);
            this._symMultiplier.alpha = 1;
            this._symMultiplier.position.set(10, -30);
        }
    }

    public showLaunchMulti(): gsap.core.Timeline {
        const tl: gsap.core.Timeline = gsap.timeline();
        tl.add([this.getStateAnimation(SymbolStates.SHOW_LAUNCH_MULTI),
            (): void => {
                HOTB.soundPlayer.playSound(GameSounds.MULTIPLIER_SCOPE);
            }
        ], 0);

        tl.add((): void => {
            if (this.symbolName === SymbolsList.LAUNCH || this.symbolName === SymbolsList.WILD) {
                const hotbData: HOTBData = HOTB.model.data;
                let multValue: number = hotbData.symbolSizes[this.reelId][this.reelTargetPosition];
                if (hotbData.reelLeft[this.reelTargetPosition] === FeatureList.XSPLIT) {
                    multValue = multValue / 2;
                }
                if (hotbData.reelRight[this.reelTargetPosition] === FeatureList.XSPLIT) {
                    multValue = multValue / 2;
                }
                if (multValue > 1) {
                    this.updateSymbolMultiplier(multValue);

                    if (!this._multiHitSoundPlayed) {
                        this._multiHitSoundPlayed = true;
                        HOTB.soundPlayer.playSound(GameSounds.MULTIPLIER_HIT);
                    }

                    HOTB.waysCounter.updateWaysCounterFromLaunchBtn(this.reelId, this.reelTargetPosition, multValue);
                }
            }
        }, 0.5666);
        return tl;
    }

    private init(): void {
        this.name = this.symbolName;
        this._spine = AnimUtils.createNewSpine({
            path: this.symbolName,
            name: this.symbolName,
            pos: new PIXI.Point(0, 0),
            initShow: true
        });
        this.addChild(this._spine);
        if (this.isNudgeWild) {
            const hotbData: HOTBData = HOTB.model.data;
            let multValue: number = 0;
            if (HOTB.model.isActionSpinToBonus) {
                multValue = hotbData.nudgeMultipliersCurrent[this.reelId];
            } else if (HOTB.model.gameData) {
                multValue = hotbData.nudgeMultipliersNext[this.reelId];
            } else {
                multValue = hotbData.nudgeMultipliers[this.reelId];
            }
            this._nudgeMultiplier = new SymbolMultiplier(multValue);
            this.addChild(this._nudgeMultiplier);
        }
        if (this.symbolName === SymbolsList.LAUNCH || this.symbolName === SymbolsList.WILD) {
            this._symMultiplier = new SymbolMultiplier(1);
            this.addChild(this._symMultiplier);
        }
    }
}